import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContractCard = ({ contract, onQuickAction }) => {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'expired':
        return 'bg-error/10 text-error border-error/20';
      case 'draft':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'expired':
        return 'AlertCircle';
      case 'draft':
        return 'Edit3';
      default:
        return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilExpiration = (expirationDate) => {
    const today = new Date();
    const expiry = new Date(expirationDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiration = getDaysUntilExpiration(contract?.expirationDate);

  return (
    <div className="group bg-card border border-border rounded-lg shadow-legal hover:shadow-legal-md transition-legal overflow-hidden">
      {/* Card Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <Link 
              to="/contract-details" 
              className="block hover:text-primary transition-legal-fast"
            >
              <h3 className="text-lg font-semibold text-foreground truncate mb-1">
                {contract?.title}
              </h3>
            </Link>
            <p className="text-sm text-muted-foreground font-mono">
              {contract?.contractId}
            </p>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(contract?.status)}`}>
              <Icon name={getStatusIcon(contract?.status)} size={12} />
              <span>{contract?.status}</span>
            </div>
          </div>
        </div>

        {/* Contract Type */}
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="FileText" size={14} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{contract?.type}</span>
        </div>

        {/* Parties */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Building" size={14} className="text-muted-foreground" />
            <span className="text-sm text-foreground font-medium">Client:</span>
            <span className="text-sm text-muted-foreground truncate">{contract?.client}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="User" size={14} className="text-muted-foreground" />
            <span className="text-sm text-foreground font-medium">Attorney:</span>
            <span className="text-sm text-muted-foreground truncate">{contract?.assignedAttorney}</span>
          </div>
        </div>

        {/* Key Dates */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-muted-foreground">Start Date</p>
            <p className="font-medium text-foreground">{formatDate(contract?.startDate)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Expiration</p>
            <div className="flex items-center space-x-1">
              <p className="font-medium text-foreground">{formatDate(contract?.expirationDate)}</p>
              {daysUntilExpiration <= 30 && daysUntilExpiration > 0 && (
                <Icon name="AlertTriangle" size={14} className="text-warning" />
              )}
              {daysUntilExpiration <= 0 && (
                <Icon name="AlertCircle" size={14} className="text-error" />
              )}
            </div>
            {daysUntilExpiration <= 30 && (
              <p className={`text-xs ${daysUntilExpiration <= 0 ? 'text-error' : 'text-warning'}`}>
                {daysUntilExpiration <= 0 ? 'Expired' : `${daysUntilExpiration} days left`}
              </p>
            )}
          </div>
        </div>

        {/* Contract Value */}
        <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-md">
          <span className="text-sm text-muted-foreground">Contract Value</span>
          <span className="text-lg font-semibold text-foreground">{contract?.value}</span>
        </div>
      </div>
      {/* Card Footer */}
      <div className="px-6 py-4 bg-muted/20 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={12} />
              <span>Updated {formatDate(contract?.lastModified)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileText" size={12} />
              <span>{contract?.documentCount} docs</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-legal">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onQuickAction('edit', contract)}
              title="Edit Contract"
            >
              <Icon name="Edit3" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onQuickAction('duplicate', contract)}
              title="Duplicate Contract"
            >
              <Icon name="Copy" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onQuickAction('archive', contract)}
              title="Archive Contract"
            >
              <Icon name="Archive" size={14} />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => onQuickAction('more', contract)}
              title="More Actions"
            >
              <Icon name="MoreHorizontal" size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractCard;